import { useField, FieldInputProps, FieldHelperProps } from 'formik'
import React, { RefObject, useEffect, useState, useCallback } from 'react'
import { useDebouncedValue } from '@hooks'

interface IProps {
    name: string,
    disabled?: boolean,
    /**
     * Obain reference to inner html element from higher order component.
     * Not used right now, but might become handy in future.
     * This was tested succesfully during work on scrollTo implementation in FormErrorSummaryComponent
     */
    innerRef?: RefObject<HTMLInputElement>,
    autoComplete?: 'off',
    /**
     * Debounce delay in milliseconds. When set, the field value will only update
     * in Formik after the user stops typing for the specified duration.
     * Set to `true` to use the default delay (1000ms), or specify a number for a custom delay.
     * Useful for preventing excessive API calls or heavy computations on every keystroke.
     * @default undefined (no debouncing)
     */
    debounceMs?: number | true,
}

export function DPInputText(props: IProps) {
    const [field,,helpers] = useField(props.name)
    
    if (!props.debounceMs) {
        return <input
            {...field}
            disabled={props.disabled}
            ref={props.innerRef}
            className="dp-input dp-input-text"
            autoComplete={props.autoComplete}
        />
    }

    const delay = props.debounceMs === true ? 1000 : props.debounceMs
    return <DebouncedInputText 
        {...props} 
        debounceMs={delay}
        field={field}
        helpers={helpers}
    />
}

function DebouncedInputText(props: IProps & { 
    debounceMs: number,
    field: FieldInputProps<any>,
    helpers: FieldHelperProps<any>
}) {
    const { field, helpers } = props
    const [localValue, setLocalValue] = useState(field.value || '')
    const debouncedValue = useDebouncedValue(localValue, props.debounceMs)
    
    const lastCommittedValue = React.useRef(field.value)

    useEffect(() => {
        if (debouncedValue !== field.value) {
            helpers.setValue(debouncedValue)
            lastCommittedValue.current = debouncedValue
        }
    }, [debouncedValue])

    useEffect(() => {
        if (field.value !== lastCommittedValue.current) {
            setLocalValue(field.value || '')
            lastCommittedValue.current = field.value
        }
    }, [field.value])

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value)
    }, [])

    const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        helpers.setValue(e.target.value)
        lastCommittedValue.current = e.target.value
        helpers.setTouched(true)
    }, [])

    return <input
        name={props.name}
        disabled={props.disabled}
        ref={props.innerRef}
        className="dp-input dp-input-text"
        autoComplete={props.autoComplete}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
    />
}
